import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Form from "./Form";
import List from "./List";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import { useSearchParams } from "react-router-dom";

const MasterData = () => {
  const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
  const INPUT_TYPE = "JSON";
  const RESPONSE_TYPE = "JSON";
  const sessionId = useSelector((state) => state.session.sessionId);

  const [tab, setTab] = useState("Caste");
  const [casteList, setCasteList] = useState([]);
  const [religionList, setReligionList] = useState([]);
  const [relationshipList, setRelationshipList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [editId, setEditId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("query") || "");

  const fetchMasterData = async () => {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(
      rest_data
    )}`;

    try {
      const response = await axios.get(fullUrl);
      const entryList = response.data.entry_list;

      const result = entryList.map((entry) => ({
        id: entry.id,
        ...Object.fromEntries(
          Object.values(entry.name_value_list).map((field) => [field.name, field.value])
        ),
      }));
      // Sort by most recent (prefer date_modified, fallback to date_entered)
      const sortedResult = result.sort((a, b) => {
        const dateA = new Date(a.date_modified || a.date_entered || "1970-01-01");
        const dateB = new Date(b.date_modified || b.date_entered || "1970-01-01");
        return dateB - dateA; // recent first
      });

      // Categorize
      setCasteList(sortedResult.filter((item) => item.category_c === "Caste"));
      setReligionList(sortedResult.filter((item) => item.category_c === "Religion"));
      setRelationshipList(sortedResult.filter((item) => item.category_c === "Relationship"));
    } catch (err) {
      console.error("Fetch error:", err.message);
    }
  };

  const createMasterData = async (name, category) => {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      name_value_list: [
        { name: "name", value: name },
        { name: "category_c", value: category },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(
      rest_data
    )}`;
    return await axios.post(fullUrl);
  };

  const updateMasterData = async (id, name) => {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      name_value_list: [
        { name: "id", value: id },
        { name: "name", value: name },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(
      rest_data
    )}`;
    return await axios.post(fullUrl);
  };

  const deleteMasterData = async (id) => {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      name_value_list: [
        { name: "id", value: id },
        { name: "deleted", value: "1" },
      ],
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(
      rest_data
    )}`;
    return await axios.post(fullUrl);
  };

  useEffect(() => {
    if (sessionId) {
      fetchMasterData();
    }
  }, [sessionId, tab]);

  // Keep local input in sync with URL changes
  useEffect(() => {
    setSearchInput(searchParams.get("query") || "");
  }, [searchParams]);

  // Clear filter when switching tabs
  useEffect(() => {
    setSearchInput("");
    setSearchParams({});
  }, [tab]);

  const handleDoSearch = () => {
    const q = (searchInput || "").trim();
    if (q) setSearchParams({ query: q });
    else setSearchParams({});
  };

  const handleClearFilter = () => {
    setSearchInput("");
    setSearchParams({});
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleDoSearch();
  };

  const handleSubmit = async () => {
    try {
      if (editId) {
        await updateMasterData(editId, name);
      } else {
        await createMasterData(name, tab);
      }
      setShowForm(false);
      setName("");
      setEditId(null);
      fetchMasterData();
    } catch (err) {
      alert("Submit error: " + err.message);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setName(item.name);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      await deleteMasterData(id);
      fetchMasterData();
    }
  };

  const currentData =
    tab === "Caste" ? casteList : tab === "Religion" ? religionList : relationshipList;

  const query = (searchParams.get("query") || "").toLowerCase();
  const filteredData = !query
    ? currentData
    : currentData.filter((item) => (item.name || "").toLowerCase().includes(query));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Tabs Section */}
        <MDBox
          className="mb-6"
          sx={{
            backgroundColor: "#fff",
            padding: 2,
            borderRadius: 2,
            boxShadow: 1,
          }}
        >
          <div className="flex justify-between items-center px-2">
            <div className=" text-xl font-semibold">{tab} Master Data</div>
            <div className="flex gap-4">
              {["Caste", "Religion", "Relationship"].map((type) => (
                <button
                  key={type}
                  onClick={() => setTab(type)}
                  className={`px-4 py-2 rounded ${
                    tab === type ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </MDBox>

        {/* List and Add Button Section */}
        <MDBox
          sx={{
            backgroundColor: "#fff",
            padding: 3,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <div className="flex justify-between items-center bg-blue-300 p-4 rounded mb-4">
            <div className="text-blue-950 font-medium">List of {tab}s</div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder={`Search ${tab.toLowerCase()}...`}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="bg-white text-blue-950 border border-blue-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <button
                onClick={handleDoSearch}
                className="bg-white text-blue-600 px-3 py-2 rounded hover:bg-blue-50"
              >
                Search
              </button>
              {query && (
                <button
                  onClick={handleClearFilter}
                  className="bg-white text-red-600 border border-blue-600 px-3 py-2 rounded hover:bg-blue-50"
                >
                  Clear filter
                </button>
              )}
              <button
                onClick={() => {
                  setEditId(null);
                  setName("");
                  setShowForm(true);
                }}
                className="bg-blue-50 text-blue-950 px-4 py-2 rounded"
              >
                + Add {tab}
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto">
            <List data={filteredData} onEdit={handleEdit} onDelete={handleDelete} />
          </div>
        </MDBox>

        {/* Modal Form */}
        <Form
          show={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleSubmit}
          name={name}
          setName={setName}
          editId={editId}
          tab={tab}
        />
      </MDBox>
    </DashboardLayout>
  );
};

export default MasterData;
